<script lang="ts">
	import { format } from 'svelte-i18n';

	import type { Device } from 'src/types/Device';

	import { devices, home_data } from 'src/stores';

	import EnergyMeterSelectModalV2 from './EnergyMeterSelectModalV2.svelte';

	import {
		ButtonV2,
		CircleAvatar,
		Icon,
		IconButton,
		LabelV2,
		Modal,
		Stack,
		Text,
		Tooltip,
		toast,
	} from '@Shelly/shelly-design-system';

	import { delete_home, save_home } from 'src/helpers/home_helpers';
	import { getDeviceIcon, getDeviceImage } from 'src/helpers/device_state_view_helpers';
	import { MAIN_ENERGY_METER_KEY } from './shared/main-energy-meter-helpers';

	let showModal = false;
	let saving = false;
	let showDeleteModal = false;
	let deleting = false;

	$: mainEnergyMeterId = ($home_data?.[MAIN_ENERGY_METER_KEY] as string) || null;
	$: mainEnergyMeter = mainEnergyMeterId ? (($devices[mainEnergyMeterId] as Device) ?? null) : null;
	$: meterImage = mainEnergyMeter ? getDeviceImage(mainEnergyMeter) : undefined;
	$: meterIcon = mainEnergyMeter ? getDeviceIcon(mainEnergyMeter) : undefined;
	$: meterName = mainEnergyMeter?.name || mainEnergyMeter?.type || mainEnergyMeterId;

	const handleOpenModal = (): void => {
		showModal = true;
	};

	const handleModalClose = (): void => {
		showModal = false;
	};

	const handleSaveMainEnergyMeter = async (e: CustomEvent<{ deviceId: string }>): Promise<void> => {
		saving = true;
		const response = await save_home(MAIN_ENERGY_METER_KEY, e.detail.deviceId);
		saving = false;

		if (!response?.isok) {
			toast.open({
				type: 'danger',
				title: $format('error_global', { default: 'Something went wrong!' }),
			});
			return;
		}

		showModal = false;
		toast.open({
			type: 'success',
			title: $format('label_energy_meter_connected', { default: 'Main energy meter saved' }),
			duration: 4000,
		});
	};

	const handleOpenDeleteModal = (): void => {
		showDeleteModal = true;
	};

	const handleCloseDeleteModal = (): void => {
		if (deleting) return;
		showDeleteModal = false;
	};

	const handleDeleteConfirm = async (): Promise<void> => {
		if (deleting) return;

		deleting = true;
		const response = await delete_home(MAIN_ENERGY_METER_KEY);
		deleting = false;

		if (!response?.isok) {
			toast.open({
				type: 'danger',
				title: $format('error_global', { default: 'Something went wrong!' }),
			});
			return;
		}

		showDeleteModal = false;
		toast.open({
			type: 'success',
			title: $format('toast_energy_meter_deleted', { default: 'Main energy meter removed' }),
			duration: 4000,
		});
	};
</script>

<Stack
	class="align-items-center"
	direction="column"
	directionMobile="row"
	align="between"
	alignMobile="none"
	gap="m"
>
	<div class="d-flex align-items-center gap-m">
		{#if mainEnergyMeter}
			<CircleAvatar
				size="l"
				image={meterImage}
				icon={meterIcon}
				backgroundColor={mainEnergyMeter.backgroundColor}
			/>
		{/if}

		<div>
			<Text styleType="body" class="mb-s">
				{$format('label_main_energy_meter', { default: 'Main Energy Meter' })}
			</Text>

			{#if mainEnergyMeterId}
				<div class="d-flex align-items-center flex-wrap gap-s">
					<Icon icon="fas fa-circle-check" size="s" class="text-success" />
					<Text styleType="body-small" class="text-color-muted">
						{$format('description_energy_meter_connected', {
							default: 'This home has a main energy meter connected.',
						})}
					</Text>
					{#if meterName}
						<LabelV2 value={meterName} size="m" />
					{/if}
				</div>
			{:else}
				<Text styleType="body-small" class="text-color-muted">
					{$format('description_no_energy_meter_added', {
						default: 'No main energy meter has been added to this home yet.',
					})}
				</Text>
			{/if}
		</div>
	</div>

	<div class="width-md-auto width-sm-full">
		{#if mainEnergyMeterId}
			<div class="d-flex align-items-center justify-content-end gap-s">
				<Tooltip
					title={$format('button_edit_main_energy_meter', {
						default: 'Change main energy meter',
					})}
					placement="top"
				>
					<IconButton
						variant="secondary"
						icon="fas fa-pen"
						aria-label={$format('button_edit_main_energy_meter', {
							default: 'Change main energy meter',
						})}
						on:click={handleOpenModal}
					/>
				</Tooltip>
				<Tooltip
					title={$format('button_delete_main_energy_meter', {
						default: 'Delete main energy meter',
					})}
					placement="top"
				>
					<IconButton
						variant="danger-secondary"
						shape="circle"
						icon="fas fa-trash"
						aria-label={$format('button_delete_main_energy_meter', {
							default: 'Delete main energy meter',
						})}
						on:click={handleOpenDeleteModal}
					/>
				</Tooltip>
			</div>
		{:else}
			<ButtonV2
				icon="fas fa-plus"
				variant="secondary"
				size="m"
				class="width-full width-md-auto"
				aria-label={$format('button_add_energy_meter', { default: 'Add Main Energy Meter' })}
				on:click={handleOpenModal}
			>
				{$format('button_add_energy_meter', { default: 'Add Main Energy Meter' })}
			</ButtonV2>
		{/if}
	</div>
</Stack>

<EnergyMeterSelectModalV2
	show={showModal}
	loading={saving}
	initialDeviceId={mainEnergyMeterId}
	isEdit={Boolean(mainEnergyMeterId)}
	on:close={handleModalClose}
	on:save={handleSaveMainEnergyMeter}
/>

<Modal
	showModal={showDeleteModal}
	title={$format('confirm_delete_main_energy_meter_title', {
		default: 'Delete main energy meter?',
	})}
	backdropClose={!deleting}
	on:close={handleCloseDeleteModal}
>
	<svelte:fragment slot="content">
		<Text styleType="body">
			{$format('confirm_delete_main_energy_meter_message', {
				default: 'This home will no longer have a main energy meter set.',
			})}
		</Text>
	</svelte:fragment>

	<div slot="footer" class="d-flex justify-content-end gap-s">
		<ButtonV2
			variant="secondary"
			aria-label={$format('button_cancel', { default: 'Cancel' })}
			class="min-width-md-m min-width-sm-auto"
			disabled={deleting}
			on:click={handleCloseDeleteModal}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</ButtonV2>
		<ButtonV2
			variant="danger"
			aria-label={$format('button_delete', { default: 'Delete' })}
			class="min-width-md-m min-width-sm-auto"
			loading={deleting}
			disabled={deleting}
			on:click={handleDeleteConfirm}
		>
			{$format('button_delete', { default: 'Delete' })}
		</ButtonV2>
	</div>
</Modal>
